import { HttpClient, HttpErrorResponse, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';

/**
 * Llamadas de la CAJA del modelo viejo a la pasarela de integración (gateway).
 *
 * 📍 Una caja real de Mundo Total no habla con el núcleo: habla con la pasarela,
 * con las credenciales de su departamento. Este servicio emula eso para la
 * pantalla `credit-save-v2`. Las credenciales NO viven en `environment`: se
 * guardan una vez en el núcleo (las ve todo el que prueba en este ambiente) y
 * quedan además en el localStorage de este navegador.
 *
 * Cabeceras: `x-api-key` y/o `Authorization: Bearer <jwt>` según el modo del
 * departamento (API_KEY_ONLY, JWT_REQUIRED o BOTH). Se mandan las que estén
 * configuradas. Nunca se devuelve ni se pinta la llave ni el token completos.
 *
 * Este servicio NO lanza errores por respuestas HTTP: devuelve siempre un
 * `ResultadoPasarela` con la URL, el código HTTP y el trace_id, para depurar.
 */

export interface CredencialesCaja {
  url: string;            // base de la pasarela (sin /api/v1)
  apiKey: string;
  identificador: string;  // login de proveedor
  clave: string;
}

export interface TokenCaja {
  token: string;
  expiraEn: number;       // epoch en milisegundos
}

export interface ResultadoPasarela {
  ok: boolean;
  url: string;
  status: number;         // 0 = no hubo respuesta (red, CORS, caída)
  traceId: string | null;
  data: any;              // cuerpo ya desenvuelto: forma del núcleo ({res} / {error})
  error: { code?: any; message: string } | null;
  reintentoLogin?: boolean;
}

// La consola que apunta al núcleo de staging le habla a la pasarela de staging; las
// demás, a la de QA. Es solo el valor inicial: la URL guardada en las credenciales manda.
export const URL_PASARELA_POR_OMISION = /staging/i.test(environment.BASE_LA || '')
  ? 'https://staging-api-integrations-gateways.creditotal.online'
  : 'https://qa-api-integrations-gateways.creditotal.online';

const CLAVE_CREDENCIALES = 'simuladorCajaVieja.credenciales';
const CLAVE_TOKEN = 'simuladorCajaVieja.token';
const MARGEN_VENCIMIENTO_MS = 10_000;

@Injectable({ providedIn: 'root' })
export class PasarelaCajaService {
  constructor(private httpClient: HttpClient) {}

  // ── Credenciales y token (localStorage) ───────────────────────────────────

  cargarCredenciales(): CredencialesCaja {
    const vacias: CredencialesCaja = { url: URL_PASARELA_POR_OMISION, apiKey: '', identificador: '', clave: '' };
    try {
      const crudo = localStorage.getItem(CLAVE_CREDENCIALES);
      if (!crudo) return vacias;
      const o = JSON.parse(crudo);
      return {
        url: String(o?.url || URL_PASARELA_POR_OMISION),
        apiKey: String(o?.apiKey || ''),
        identificador: String(o?.identificador || ''),
        clave: String(o?.clave || ''),
      };
    } catch {
      return vacias;
    }
  }

  guardarCredenciales(c: CredencialesCaja): boolean {
    try {
      localStorage.setItem(CLAVE_CREDENCIALES, JSON.stringify(c));
      return true;
    } catch {
      return false;
    }
  }

  // ── Copia compartida en el núcleo ─────────────────────────────────────────
  // La misma caja para todos los que prueban en este ambiente: se guarda una vez y la ve
  // cualquiera con sesión de la consola (GET/PUT /api/simulador-caja/credit-save-v2). Va por el
  // interceptor de siempre, que pone el token de la consola: es el núcleo, no la pasarela.

  private get urlCompartidas(): string {
    return `${environment.BASE_LA}/api/simulador-caja/credit-save-v2`;
  }

  /** Lo guardado en el núcleo, o `null` si no hay nada o no se pudo leer. */
  async cargarCompartidas(): Promise<CredencialesCaja | null> {
    try {
      const r: any = await lastValueFrom(this.httpClient.get(this.urlCompartidas));
      const d = r?.res;
      if (!d || (!d.identificador && !d.apiKey)) return null;
      return {
        url: String(d.url || URL_PASARELA_POR_OMISION),
        apiKey: String(d.apiKey || ''),
        identificador: String(d.identificador || ''),
        clave: String(d.clave || ''),
      };
    } catch {
      return null;
    }
  }

  /** Guarda en el núcleo para todos; `false` si no se pudo. */
  async guardarCompartidas(c: CredencialesCaja): Promise<boolean> {
    try {
      await lastValueFrom(this.httpClient.put(this.urlCompartidas, c));
      return true;
    } catch {
      return false;
    }
  }

  /** Borra credenciales y token guardados. */
  borrarCredenciales(): void {
    try {
      localStorage.removeItem(CLAVE_CREDENCIALES);
      localStorage.removeItem(CLAVE_TOKEN);
    } catch { /* sin localStorage no hay nada que borrar */ }
  }

  cargarToken(): TokenCaja | null {
    try {
      const crudo = localStorage.getItem(CLAVE_TOKEN);
      if (!crudo) return null;
      const o = JSON.parse(crudo);
      if (!o?.token || !o?.expiraEn) return null;
      return { token: String(o.token), expiraEn: Number(o.expiraEn) };
    } catch {
      return null;
    }
  }

  private guardarToken(t: TokenCaja | null): void {
    try {
      if (t) localStorage.setItem(CLAVE_TOKEN, JSON.stringify(t));
      else localStorage.removeItem(CLAVE_TOKEN);
    } catch { /* se sigue sin persistir */ }
  }

  tokenVigente(t: TokenCaja | null = this.cargarToken()): boolean {
    return !!t && t.expiraEn - MARGEN_VENCIMIENTO_MS > Date.now();
  }

  /** Enmascara un secreto: deja solo los 4 últimos caracteres. */
  enmascarar(valor: string | null | undefined): string {
    if (!valor) return '';
    if (valor.length <= 8) return '••••';
    return `••••••${valor.slice(-4)}`;
  }

  /**
   * Qué falta para poder llamar, en castellano, o `null` si está todo.
   * Hace falta la URL y al menos un medio de autenticarse.
   */
  faltante(c: CredencialesCaja = this.cargarCredenciales()): string | null {
    if (!c.url?.trim()) return 'la URL de la pasarela';
    const tieneLogin = !!c.identificador?.trim() && !!c.clave;
    if (!c.apiKey?.trim() && !tieneLogin && !this.tokenVigente()) {
      return 'una x-api-key, o el identificador y la clave del login de proveedor';
    }
    return null;
  }

  // ── Login de proveedor ────────────────────────────────────────────────────

  async iniciarSesion(c: CredencialesCaja = this.cargarCredenciales()): Promise<ResultadoPasarela> {
    const url = `${this.base(c)}/auth/provider-login`;
    const r = await this.enviar('POST', url, { identifier: c.identificador, password: c.clave }, this.cabecerasBase(c));
    // `armarResultado` desenvuelve {ok, data:X} como { res: X }: el token queda en `res`.
    const d = r.data?.res ?? r.data?.data ?? r.data;
    const token = d?.token;
    if (r.ok && token) {
      const seg = Number(d?.expiresIn) || 3600;
      this.guardarToken({ token, expiraEn: Date.now() + seg * 1000 });
    } else if (r.ok) {
      r.ok = false;
      r.error = { message: 'El login respondió sin token.' };
    }
    return r;
  }

  // ── Los tres servicios de la caja vieja ───────────────────────────────────

  consultarCupo(body: any): Promise<ResultadoPasarela> {
    return this.llamar('/gateway/credits/client-quota', body);
  }

  crearCredito(body: any, idempotencyKey: string = this.nuevoUuid()): Promise<ResultadoPasarela> {
    return this.llamar('/gateway/credits/create', body, idempotencyKey);
  }

  registrarInicial(body: any, idempotencyKey: string = this.nuevoUuid()): Promise<ResultadoPasarela> {
    return this.llamar('/gateway/payments/initial', body, idempotencyKey);
  }

  nuevoUuid(): string {
    const c: any = (globalThis as any).crypto;
    if (c?.randomUUID) return c.randomUUID();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
      const r = (Math.random() * 16) | 0;
      return (ch === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
  }

  // ── Interno ───────────────────────────────────────────────────────────────

  private base(c: CredencialesCaja): string {
    const limpia = (c.url || URL_PASARELA_POR_OMISION).trim().replace(/\/+$/, '').replace(/\/api\/v1$/, '');
    return `${limpia}/api/v1`;
  }

  private cabecerasBase(c: CredencialesCaja): { [k: string]: string } {
    // La sesión de la consola NO viaja a la pasarela: sin esta marca el interceptor le pega el
    // x-token del usuario (el del núcleo) a cada llamada que no traiga su propio Authorization,
    // como el login de proveedor o una caja que sólo usa x-api-key.
    const h: { [k: string]: string } = { 'content-type': 'application/json', 'X-Skip-Auth-Interceptor': '1' };
    if (c.apiKey?.trim()) h['x-api-key'] = c.apiKey.trim();
    return h;
  }

  private tieneLogin(c: CredencialesCaja): boolean {
    return !!c.identificador?.trim() && !!c.clave;
  }

  /**
   * Llama a la pasarela con las credenciales guardadas. Si hay login configurado
   * y no hay token vigente, lo obtiene antes. Si la respuesta es 401 y hay login,
   * re-loguea UNA vez y reintenta.
   */
  private async llamar(ruta: string, body: any, idempotencyKey?: string): Promise<ResultadoPasarela> {
    const c = this.cargarCredenciales();
    const url = `${this.base(c)}${ruta}`;

    const falta = this.faltante(c);
    if (falta) {
      return { ok: false, url, status: 0, traceId: null, data: null, error: { message: `Falta ${falta}.` } };
    }

    if (this.tieneLogin(c) && !this.tokenVigente()) {
      const login = await this.iniciarSesion(c);
      if (!login.ok) {
        login.error = { ...login.error, message: `No se pudo iniciar sesión de proveedor: ${login.error?.message || 'sin detalle'}` };
        return login;
      }
    }

    const armar = () => {
      const h = this.cabecerasBase(c);
      const t = this.cargarToken();
      if (this.tokenVigente(t)) h['Authorization'] = `Bearer ${t!.token}`;
      if (idempotencyKey) h['idempotency-key'] = idempotencyKey;
      return h;
    };

    let r = await this.enviar('POST', url, body, armar());
    if (r.status === 401 && this.tieneLogin(c)) {
      const login = await this.iniciarSesion(c);
      if (login.ok) {
        r = await this.enviar('POST', url, body, armar());
        r.reintentoLogin = true;
      }
    }
    return r;
  }

  private async enviar(metodo: 'POST', url: string, body: any, cabeceras: { [k: string]: string }): Promise<ResultadoPasarela> {
    try {
      const resp: HttpResponse<any> = await lastValueFrom(
        this.httpClient.request<any>(metodo, url, { body, headers: new HttpHeaders(cabeceras), observe: 'response' })
      );
      return this.armarResultado(url, resp.status, resp.body, resp.headers);
    } catch (e: any) {
      const err = e as HttpErrorResponse;
      return this.armarResultado(url, err?.status ?? 0, err?.error, err?.headers, true);
    }
  }

  private armarResultado(url: string, status: number, body: any, headers?: any, falloHttp = false): ResultadoPasarela {
    const traceId: string | null =
      body?.error?.trace_id || body?.trace_id || body?.data?.trace_id ||
      headers?.get?.('x-trace-id') || headers?.get?.('x-request-id') || null;

    // Error envuelto de la pasarela: {ok:false, error:{code, message}}
    // o error del núcleo: {msg} / {error:{code, msg}}
    let error: ResultadoPasarela['error'] = null;
    if (body?.ok === false || falloHttp) {
      const e = body?.error;
      const mensaje =
        (typeof e === 'string' ? e : null) || e?.message || e?.msg || body?.message || body?.msg ||
        (status === 0 ? 'No hubo respuesta de la pasarela (red, CORS o caída).' : `La pasarela respondió HTTP ${status}.`);
      error = { code: e?.code, message: String(mensaje) };
    }

    // Desenvolver: {ok:true, data:X} → forma del núcleo. Si X ya trae `res` o
    // `error` se deja tal cual; si no, X es el resultado y se envuelve en `res`.
    let data: any = body;
    if (!error && body?.ok === true && body?.data !== undefined) {
      const d = body.data;
      data = d && typeof d === 'object' && ('res' in d || 'error' in d) ? d : { res: d };
    }

    return { ok: !error && status >= 200 && status < 300, url, status, traceId, data, error };
  }
}
